// ─────────────────────────────────────────────────────────────────────────────
// MatriceSettimanale — vista alternativa alla classica per la sezione Attività.
// Mostra una griglia paper-friendly con righe = voci di scadenziario a frequenza
// giornaliera (raggruppate per ambito) e colonne = giorni della settimana.
//
// Tap su una cella di oggi/giorno passato non compilato → registra (FormValore
// per voci numeriche, conferma diretta per booleane). Tap su "NC" apre FormNC.
// Le settimane passate sono navigabili in sola lettura, le future non
// interagibili.
//
// Le registrazioni "tardive" (cioè su un giorno passato) NON sono marcate
// visivamente: per scelta di prodotto, una cella compilata appare identica
// a una compilata in tempo reale.
//
// Il componente è autonomo dal punto di vista del fetching ma riusa i modali
// esistenti FormValore / FormNC / ModalProponiAllegato passati come prop dal
// componente padre, per evitare duplicazione di codice.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { LT } from './ui/tokens';
import I, { Ico } from './ui/icons';
import { Btn } from './ui/components';

const GIORNI_LABEL = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

// Helpers data ────────────────────────────────────────────────────────────────
function pad2(n)       { return String(n).padStart(2, '0'); }
function isoDate(d)    { return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; }
function parseIso(s)   { const [y, m, d] = String(s).split('-').map(n => parseInt(n, 10)); return new Date(y, m - 1, d); }
function lunediOf(d)   { const c = new Date(d); const dow = c.getDay(); const diff = (dow === 0 ? -6 : 1 - dow); c.setDate(c.getDate() + diff); c.setHours(0, 0, 0, 0); return c; }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function fmtBreve(d)   { return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }); }
function fmtRange(lun, dom) {
  const sameMonth = lun.getMonth() === dom.getMonth();
  if (sameMonth) {
    return `${lun.getDate()} - ${dom.getDate()} ${dom.toLocaleDateString('it-IT', { month: 'long' })} ${dom.getFullYear()}`;
  }
  return `${lun.getDate()} ${lun.toLocaleDateString('it-IT', { month: 'short' })} - ${dom.getDate()} ${dom.toLocaleDateString('it-IT', { month: 'short' })} ${dom.getFullYear()}`;
}
function inizialiNome(nomeCognome) {
  if (!nomeCognome) return '';
  const parts = String(nomeCognome).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase() + '.';
  return parts.map(p => p.charAt(0).toUpperCase() + '.').join('');
}
function fmtOraBreve(ora) {
  if (!ora) return '';
  const s = String(ora);
  return s.length >= 5 ? s.substring(0, 5) : s;
}

export default function MatriceSettimanale({
  operatore,
  azienda,
  apiFetch,
  apiBase,
  FormValore,
  FormNC,
  ModalProponiAllegato,
  toast,
}) {
  const oggiIso = isoDate(new Date());

  const [lunediIso, setLunediIso] = useState(isoDate(lunediOf(new Date())));
  const [voci,         setVoci]         = useState([]);
  const [esecuzioni,   setEsecuzioni]   = useState([]);
  const [caricando,    setCaricando]    = useState(false);
  const [errore,       setErrore]       = useState('');
  const [_eseguendo,   setEseguendo]    = useState(null);
  const [valoreAperto, setValoreAperto] = useState(null);
  const [ncAperta,     setNcAperta]     = useState(null);
  const [allegatiProposta, setAllegatiProposta] = useState(null);
  const [filtroAmbito, setFiltroAmbito] = useState('tutti');
  const [sessione,           setSessione]           = useState(null); // null | { data, ora }
  const [mostraFormSessione, setMostraFormSessione] = useState(false);
  const [nuovaData,          setNuovaData]          = useState('');
  const [nuovaOra,           setNuovaOra]           = useState('');
  const [erroreSessione,     setErroreSessione]     = useState('');
  const [oraCorrente,        setOraCorrente]        = useState(new Date());

  // Data "attiva" — se in sessione e' la data della sessione, altrimenti oggi reale
  const dataAttiva = sessione ? sessione.data : oggiIso;

  const giorniSettimana = useMemo(() => {
    const lun = parseIso(lunediIso);
    return Array.from({ length: 7 }, (_, i) => addDays(lun, i));
  }, [lunediIso]);

  const ambitiDisponibili = useMemo(() => {
    return [...new Set(voci.map(v => v.ambito || 'Altro'))];
  }, [voci]);

  const isSettimanaCorrente = lunediIso === isoDate(lunediOf(parseIso(dataAttiva)));
  const isSettimanaFutura   = parseIso(lunediIso) > lunediOf(parseIso(dataAttiva));

  const carica = useCallback(async (lunIso) => {
    setCaricando(true);
    setErrore('');
    try {
      const url = `${apiBase}/attivita/matrice-settimanale/${operatore.id}?settimana=${lunIso}`;
      const res = await apiFetch(url);
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      setVoci(data.voci || []);
      setEsecuzioni(data.esecuzioni || []);
    } catch (e) {
      console.error(e);
      setErrore('Errore nel caricamento della matrice');
    }
    setCaricando(false);
  }, [apiBase, apiFetch, operatore.id]);

  useEffect(() => {
    carica(lunediIso);
  }, [lunediIso, carica]);

  // Aggiorna l'orologio ogni 30 secondi (solo per visualizzazione del banner)
  useEffect(() => {
    const t = setInterval(() => setOraCorrente(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // Quando si avvia una sessione con una data diversa, salta automaticamente
  // alla settimana che contiene quella data
  useEffect(() => {
    if (sessione && sessione.data) {
      const targetLun = isoDate(lunediOf(parseIso(sessione.data)));
      if (targetLun !== lunediIso) setLunediIso(targetLun);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessione]);

  const indiceEsec = useMemo(() => {
    const m = new Map();
    for (const e of esecuzioni) {
      const dStr = typeof e.data_effettuazione === 'string'
        ? e.data_effettuazione.slice(0, 10)
        : isoDate(new Date(e.data_effettuazione));
      m.set(`${e.id_scadenziario}|${dStr}`, e);
    }
    return m;
  }, [esecuzioni]);

  const vociFiltrate = useMemo(() => {
    return filtroAmbito === 'tutti' ? voci : voci.filter(v => (v.ambito || 'Altro') === filtroAmbito);
  }, [voci, filtroAmbito]);

  const gruppi = useMemo(() => {
    // Doppio raggruppamento: ambito → tipo_attivita.
    // out = [ { ambito, totale, tipi: [ { tipo, voci: [...] } ] } ]
    const out = [];
    const ambIdx = new Map();
    for (const v of vociFiltrate) {
      const ambKey  = v.ambito || 'Altro';
      const tipoKey = v.tipo_attivita || '—';
      let amb;
      if (!ambIdx.has(ambKey)) {
        amb = { ambito: ambKey, totale: 0, tipi: [], tipiIdx: new Map() };
        ambIdx.set(ambKey, amb);
        out.push(amb);
      } else {
        amb = ambIdx.get(ambKey);
      }
      amb.totale++;
      if (!amb.tipiIdx.has(tipoKey)) {
        amb.tipiIdx.set(tipoKey, { tipo: tipoKey, voci: [] });
        amb.tipi.push(amb.tipiIdx.get(tipoKey));
      }
      amb.tipiIdx.get(tipoKey).voci.push(v);
    }
    // rimuovo la Map prima del rendering (non più serve)
    out.forEach(g => { delete g.tipiIdx; });
    return out;
  }, [vociFiltrate]);

  const settimanaPrec = () => {
    const nuovo = addDays(parseIso(lunediIso), -7);
    setLunediIso(isoDate(nuovo));
  };
  const settimanaSucc = () => {
    const nuovoLun = addDays(parseIso(lunediIso), 7);
    if (nuovoLun > lunediOf(parseIso(dataAttiva))) return;
    setLunediIso(isoDate(nuovoLun));
  };
  const tornaOggi = () => setLunediIso(isoDate(lunediOf(parseIso(dataAttiva))));

  function statoCella(voce, dataIso) {
    const exec = indiceEsec.get(`${voce.id_scadenziario}|${dataIso}`);
    if (exec) {
      if (exec.saltata) return { stato: 'compilata-saltata', esec: exec };
      return { stato: exec.esito_conforme ? 'compilata-conforme' : 'compilata-nc', esec: exec };
    }
    const dGiorno = parseIso(dataIso);
    const dActiva = parseIso(dataAttiva);
    const dow = dGiorno.getDay();
    const isWeekendSaltato = (dow === 6 && voce.salta_sabato) || (dow === 0 && voce.salta_domenica);
    if (isWeekendSaltato) return { stato: 'salta-weekend' };
    if (dGiorno > dActiva) return { stato: 'futura' };
    if (dataIso === dataAttiva) return { stato: 'da-fare-oggi' };
    return { stato: 'da-fare-passato' };
  }

  function costruisciAttivitaPerModale(voce) {
    return {
      id_scadenziario: voce.id_scadenziario,
      sub_attivita: voce.sub_attivita,
      valore_min: voce.valore_min,
      valore_max: voce.valore_max,
      oggetto: voce.oggetto,
      collocazione: voce.collocazione,
      ambito: voce.ambito,
      tipo_attivita: voce.tipo_attivita,
      registro_speciale: voce.registro_speciale,
    };
  }

  async function handleTapSalta(voce) {
    await registraSalta(voce);
  }

  async function registraSalta(voce) {
    const dataDaUsare = sessione ? sessione.data : oggiIso;
    const oraDaUsare  = sessione ? sessione.ora  : oraOraIso();
    const key = `${voce.id_scadenziario}|${dataDaUsare}`;
    setEseguendo(key);
    try {
      const res = await apiFetch(`${apiBase}/attivita/salta`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idScadenziario: voce.id_scadenziario,
          idOperatore: operatore.id,
          dataCustom: dataDaUsare,
          oraCustom: oraDaUsare,
          note: null,
        }),
      });
      if (!res.ok) throw new Error();
      toast('Attività marcata come N.A.');
      await carica(lunediIso);
    } catch {
      toast('Errore nella registrazione', 'errore');
    }
    setEseguendo(null);
  }

  async function handleTapConforme(voce) {
    const haLimiti = voce.valore_min !== null || voce.valore_max !== null;
    const att = costruisciAttivitaPerModale(voce);
    if (haLimiti) {
      // Numerica: apre FormValore. Data e ora vengono fissate automaticamente
      // (sessione attiva o orologio reale) — l'utente inserisce solo il valore.
      setValoreAperto({ voce, attivita: att, intento: 'conforme' });
    } else {
      // Booleana: registrazione istantanea, niente modale.
      await registraConforme(voce);
    }
  }

  function handleTapNonConforme(voce) {
    const haLimiti = voce.valore_min !== null || voce.valore_max !== null;
    const att = costruisciAttivitaPerModale(voce);
    if (haLimiti) {
      setValoreAperto({ voce, attivita: att, intento: 'nonconforme' });
    } else {
      const descr = `${voce.tipo_attivita} - ${voce.oggetto} (${voce.collocazione || ''}): esito non conforme`;
      const oraCorr = sessione ? sessione.ora : oraOraIso();
      setNcAperta({ voce, attivita: att, ora: oraCorr, eventoPrecompilato: descr, valore: null });
    }
  }

  function oraOraIso() {
    const d = new Date();
    return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  }

  async function registraConforme(voce) {
    const dataDaUsare = sessione ? sessione.data : oggiIso;
    const oraDaUsare  = sessione ? sessione.ora  : oraOraIso();
    const key = `${voce.id_scadenziario}|${dataDaUsare}`;
    setEseguendo(key);
    try {
      const res = await apiFetch(`${apiBase}/attivita/esegui`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idScadenziario: voce.id_scadenziario,
          idOperatore: operatore.id,
          esitoConforme: true,
          esitoValore: null,
          note: null,
          dataCustom: dataDaUsare,
          oraCustom: oraDaUsare,
        }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      toast('✓ Attività registrata!');
      if (voce.registro_speciale) {
        setAllegatiProposta({ idAttivita: data.id, registroSpeciale: voce.registro_speciale });
      }
      await carica(lunediIso);
    } catch {
      toast('Errore nella registrazione', 'errore');
    }
    setEseguendo(null);
  }

  async function handleValoreConferma(valore, neiLimiti, dataCustom, oraCustom) {
    const ctx = valoreAperto;
    setValoreAperto(null);
    if (!ctx) return;
    const dataAttivaSnap = sessione ? sessione.data : oggiIso;
    const oraAttivaSnap  = sessione ? sessione.ora  : oraOraIso();
    const dataDaUsare = dataCustom || dataAttivaSnap;
    const oraDaUsare  = oraCustom  || oraAttivaSnap;

    if (ctx.intento === 'conforme') {
      const key = `${ctx.voce.id_scadenziario}|${dataDaUsare}`;
      setEseguendo(key);
      try {
        const res = await apiFetch(`${apiBase}/attivita/esegui`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            idScadenziario: ctx.voce.id_scadenziario,
            idOperatore: operatore.id,
            esitoConforme: true,
            esitoValore: valore,
            note: null,
            dataCustom: dataDaUsare,
            oraCustom: oraDaUsare,
          }),
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        toast('✓ Attività registrata!');
        if (ctx.voce.registro_speciale) {
          setAllegatiProposta({ idAttivita: data.id, registroSpeciale: ctx.voce.registro_speciale });
        }
        await carica(lunediIso);
      } catch {
        toast('Errore nella registrazione', 'errore');
      }
      setEseguendo(null);
    } else {
      const um = '';
      let violazione = '';
      if (ctx.voce.valore_min !== null && valore < ctx.voce.valore_min) violazione = ' (valore inferiore al minimo)';
      else if (ctx.voce.valore_max !== null && valore > ctx.voce.valore_max) violazione = ' (valore superiore al massimo)';
      const descr =
        `${ctx.voce.tipo_attivita} - ${ctx.voce.oggetto} (${ctx.voce.collocazione || ''}): ` +
        `valore misurato ${valore}${um}${violazione}` +
        (ctx.voce.valore_min !== null ? ` | limite min: ${ctx.voce.valore_min}${um}` : '') +
        (ctx.voce.valore_max !== null ? ` | limite max: ${ctx.voce.valore_max}${um}` : '');
      setNcAperta({
        voce: ctx.voce,
        attivita: costruisciAttivitaPerModale(ctx.voce),
        dataIso: dataDaUsare,
        ora: oraDaUsare,
        eventoPrecompilato: descr,
        valore,
      });
    }
  }

  function stileCella(stato) {
    const base = {
      width: '100%', height: 50, borderRadius: LT.r.md,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      fontSize: 13, fontWeight: 700, fontFamily: LT.sans, lineHeight: 1.1,
      cursor: 'default', userSelect: 'none',
      transition: 'transform .12s ease, box-shadow .12s ease',
      border: `1px solid ${LT.n200}`,
      background: '#fff', color: LT.n400,
      boxSizing: 'border-box',
    };
    switch (stato) {
      case 'compilata-conforme':
        return { ...base, background: LT.verdeSoft, color: LT.verde, borderColor: 'transparent', cursor: 'pointer' };
      case 'compilata-nc':
        return { ...base, background: LT.rossoSoft, color: LT.rosso, borderColor: 'transparent', cursor: 'pointer' };
      case 'compilata-saltata':
        return { ...base, background: LT.n100, color: LT.n600, borderColor: 'transparent', cursor: 'default' };
      case 'da-fare-oggi':
        return { ...base, background: LT.bluSoft, color: LT.blu, borderColor: LT.blu, cursor: 'pointer', borderStyle: 'dashed' };
      case 'da-fare-passato':
        return { ...base, background: '#FFF7ED', color: LT.arancio, borderColor: LT.arancio + '55' };
      case 'salta-weekend':
        return { ...base, background: LT.n100, color: LT.n400, borderColor: LT.n150 };
      case 'futura':
      default:
        return { ...base, background: '#fff', color: LT.n300, borderColor: LT.n150 };
    }
  }

  function CellaContent({ stato, esec, voce }) {
    if (stato === 'compilata-saltata') {
      const ini = esec ? inizialiNome(esec.operatore_nome) : '';
      const ora = esec ? fmtOraBreve(esec.ora_effettuazione) : '';
      const infoLine = (ini || ora) ? `${ini}${ini && ora ? ' · ' : ''}${ora}` : '';
      return (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.05 }}>N.A.</div>
          {infoLine && (
            <div style={{ fontSize: 9, fontWeight: 500, marginTop: 2, opacity: .65, lineHeight: 1 }}>
              {infoLine}
            </div>
          )}
        </>
      );
    }
    if (stato === 'compilata-conforme' || stato === 'compilata-nc') {
      const valore = esec ? esec.esito_valore : null;
      const haLimiti = voce.valore_min !== null || voce.valore_max !== null;
      const ini = esec ? inizialiNome(esec.operatore_nome) : '';
      const ora = esec ? fmtOraBreve(esec.ora_effettuazione) : '';
      const infoLine = (ini || ora) ? `${ini}${ini && ora ? ' · ' : ''}${ora}` : '';
      let principale;
      if (haLimiti && valore !== null && valore !== undefined) {
        const num = parseFloat(valore);
        principale = Number.isFinite(num) ? num.toString().replace('.', ',') : String(valore);
      } else {
        principale = stato === 'compilata-conforme' ? 'OK' : 'KO';
      }
      return (
        <>
          <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.05 }}>{principale}</div>
          {infoLine && (
            <div style={{ fontSize: 9, fontWeight: 500, marginTop: 2, opacity: .65, lineHeight: 1 }}>
              {infoLine}
            </div>
          )}
          {stato === 'compilata-nc' && esec && esec.nc_progressivo != null && (
            <div style={{ fontSize: 9, fontWeight: 600, marginTop: 1, opacity: .85, lineHeight: 1 }}>
              NC #{esec.nc_progressivo}
            </div>
          )}
        </>
      );
    }
    if (stato === 'da-fare-oggi') {
      const haLimiti = voce.valore_min !== null || voce.valore_max !== null;
      return <div style={{ fontSize: 12, fontWeight: 600 }}>{haLimiti ? 'scrivi…' : 'tap'}</div>;
    }
    if (stato === 'da-fare-passato') {
      return <div style={{ fontSize: 11, fontWeight: 600 }}>manca</div>;
    }
    if (stato === 'salta-weekend') {
      return <div style={{ fontSize: 10, fontWeight: 500 }}>—</div>;
    }
    return <div style={{ fontSize: 14, color: LT.n300 }}>·</div>;
  }

  function handleClickCella(voce, dataIso, stato) {
    if (stato === 'futura' || stato === 'salta-weekend') return;
    if (stato === 'compilata-conforme' || stato === 'compilata-nc') return;
    if (stato === 'da-fare-passato') return; // celle "manca" passate sono solo informative; chiudere il buco via sessione
    if (stato === 'da-fare-oggi') handleTapConforme(voce);
  }

  return (
    <div>
      {valoreAperto && (
        <FormValore
          attivita={valoreAperto.attivita}
          intento={valoreAperto.intento}
          onConferma={handleValoreConferma}
          onAnnulla={() => setValoreAperto(null)}
          sessioneData={sessione ? sessione.data : oggiIso}
          sessioneOra={sessione ? sessione.ora  : oraOraIso()}
        />
      )}

      {ncAperta && (
        <FormNC
          attivita={ncAperta.attivita}
          operatore={operatore}
          eventoPrecompilato={ncAperta.eventoPrecompilato || ''}
          valore={ncAperta.valore}
          dataCustom={(sessione ? sessione.data : oggiIso) || ''}
          oraCustom={(sessione ? sessione.ora : (ncAperta.ora || oraOraIso())) || ''}
          onConferma={(idAttivita) => {
            const reg = ncAperta.voce ? ncAperta.voce.registro_speciale : null;
            setNcAperta(null);
            carica(lunediIso);
            if (reg && idAttivita) setAllegatiProposta({ idAttivita, registroSpeciale: reg });
          }}
          onAnnulla={() => setNcAperta(null)}
        />
      )}

      {allegatiProposta && ModalProponiAllegato && (
        <ModalProponiAllegato
          idAttivita={allegatiProposta.idAttivita}
          registroSpeciale={allegatiProposta.registroSpeciale}
          operatore={operatore}
          onFine={() => setAllegatiProposta(null)}
        />
      )}

      {/* Modale avvio sessione */}
      {mostraFormSessione && (
        <FormSessione
          initData={nuovaData}
          initOra={nuovaOra}
          oggiIso={oggiIso}
          errore={erroreSessione}
          onAnnulla={() => { setMostraFormSessione(false); setErroreSessione(''); }}
          onConferma={(d, o) => {
            if (!d || !o) { setErroreSessione("Inserisci sia la data che l'ora"); return; }
            // Vieta date future (oltre oggi reale)
            if (parseIso(d) > parseIso(oggiIso)) {
              setErroreSessione('Non puoi avviare una sessione nel futuro');
              return;
            }
            setSessione({ data: d, ora: o });
            setMostraFormSessione(false);
            setErroreSessione('');
          }}
        />
      )}

      {/* Banner orologio (no sessione) o sessione attiva */}
      {sessione ? (
        <div style={{
          background: LT.bluSoft, border: `1px solid ${LT.blu}22`,
          borderRadius: LT.r.md, padding: '10px 14px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 32, height: 32, borderRadius: LT.r.md,
            background: LT.blu, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.clock} size={18} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: LT.blu }}>Sessione attiva</div>
            <div style={{ fontSize: 12, color: LT.blu, marginTop: 1 }}>
              {sessione.data.split('-').reverse().join('/')} alle {sessione.ora}
            </div>
          </div>
          <Btn kind="outline" size="sm" icon="x" onClick={() => setSessione(null)}
            style={{ borderColor: LT.rosso, color: LT.rosso }}>
            Termina
          </Btn>
        </div>
      ) : (
        <div style={{
          background: '#fff', border: `1px solid ${LT.n150}`,
          borderRadius: LT.r.md, padding: '8px 12px', marginBottom: 12,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <div style={{
            width: 28, height: 28, borderRadius: LT.r.sm,
            background: LT.n100, color: LT.n600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.clock} size={15} />
          </div>
          <div style={{ flex: 1, minWidth: 0, fontSize: 12, color: LT.n600 }}>
            {oraCorrente.toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })} ·{' '}
            <strong style={{ color: LT.n900 }}>
              {String(oraCorrente.getHours()).padStart(2, '0')}:{String(oraCorrente.getMinutes()).padStart(2, '0')}
            </strong>
          </div>
          <Btn kind="soft" size="sm" icon="clock" onClick={() => {
            const now = new Date();
            const pad = n => String(n).padStart(2, '0');
            setNuovaData(`${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`);
            setNuovaOra(`${pad(now.getHours())}:${pad(now.getMinutes())}`);
            setMostraFormSessione(true);
          }}>
            Avvia sessione
          </Btn>
        </div>
      )}

      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
        flexWrap: 'wrap',
      }}>
        <Btn kind="outline" size="sm" onClick={settimanaPrec}>‹ Prec.</Btn>
        <div style={{
          flex: 1, minWidth: 180, textAlign: 'center', fontSize: 14, fontWeight: 700, color: LT.n800,
        }}>
          Settimana {fmtRange(parseIso(lunediIso), addDays(parseIso(lunediIso), 6))}
          {!isSettimanaCorrente && (
            <div style={{ fontSize: 11, fontWeight: 500, color: LT.n500, marginTop: 2 }}>
              {isSettimanaFutura ? 'futura · sola lettura' : 'sola lettura'}
            </div>
          )}
        </div>
        <Btn kind="outline" size="sm" onClick={settimanaSucc} disabled={isSettimanaCorrente}>Succ. ›</Btn>
        {!isSettimanaCorrente && (
          <Btn kind="soft" size="sm" onClick={tornaOggi}>Oggi</Btn>
        )}
      </div>

      {ambitiDisponibili.length > 1 && (
        <div style={{
          display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12,
          paddingBottom: 4, WebkitOverflowScrolling: 'touch',
        }}>
          {['tutti', ...ambitiDisponibili].map(a => {
            const on = filtroAmbito === a;
            return (
              <button
                key={a}
                onClick={() => setFiltroAmbito(a)}
                style={{
                  flexShrink: 0, padding: '6px 14px', borderRadius: 999,
                  border: `1px solid ${on ? LT.blu : LT.n200}`,
                  background: on ? LT.blu : '#fff',
                  color: on ? '#fff' : LT.n600,
                  fontSize: 12, fontWeight: on ? 700 : 500,
                  cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: LT.sans,
                }}
              >
                {a === 'tutti' ? 'Tutti' : a}
              </button>
            );
          })}
        </div>
      )}

      {errore && (
        <div style={{
          background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
          padding: '10px 14px', marginBottom: 12, fontSize: 13, fontWeight: 500,
        }}>
          {errore}
        </div>
      )}

      {gruppi.length > 0 && (
        <div style={{
          background: '#fff', border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
          overflow: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: LT.sh1,
        }}>
          <table style={{
            width: '100%', borderCollapse: 'separate', borderSpacing: 0,
            minWidth: 800, fontFamily: LT.sans,
          }}>
            <thead>
              <tr>
                <th style={thVoce}>Voce di scadenziario</th>
                {giorniSettimana.map((d, i) => {
                  const isOggi = isoDate(d) === oggiIso;
                  return (
                    <th key={i} style={{ ...thGiorno, ...(isOggi ? styleThOggi : null) }}>
                      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase' }}>
                        {GIORNI_LABEL[i]}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>
                        {fmtBreve(d)}
                      </div>
                      {isOggi && (
                        <div style={{ fontSize: 10, fontWeight: 600, color: LT.blu, marginTop: 2 }}>
                          oggi
                        </div>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {gruppi.map(g => (
                <React.Fragment key={g.ambito}>
                  <tr>
                    <td colSpan={8} style={tdAmbito}>
                      {g.ambito} <span style={{ color: LT.n500, fontWeight: 500, fontSize: 11, marginLeft: 6 }}>
                        ({g.totale} {g.totale === 1 ? 'voce' : 'voci'})
                      </span>
                    </td>
                  </tr>
                  {g.tipi.map(tg => (
                    <React.Fragment key={g.ambito + '|' + tg.tipo}>
                      {g.tipi.length > 1 && (
                        <tr>
                          <td colSpan={8} style={tdTipo}>
                            {tg.tipo}
                            <span style={{ color: LT.blu, opacity: 0.7, fontWeight: 600, fontSize: 10, marginLeft: 8 }}>
                              · {tg.voci.length}
                            </span>
                          </td>
                        </tr>
                      )}
                      {tg.voci.map(v => (
                    <tr key={v.id_scadenziario}>
                      <td style={tdVoce}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: LT.n900 }}>
                          {v.oggetto}
                          {v.collocazione && (
                            <span style={{ color: LT.n500, fontWeight: 400 }}> · {v.collocazione}</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: LT.n500, marginTop: 2 }}>
                          {v.tipo_attivita}
                          {v.sub_attivita && <> · {v.sub_attivita}</>}
                          {(v.valore_min !== null || v.valore_max !== null) && (
                            <span style={{ marginLeft: 6, color: LT.blu, fontWeight: 600 }}>
                              {v.valore_min !== null && `min ${v.valore_min}`}
                              {(v.valore_min !== null && v.valore_max !== null) && ' / '}
                              {v.valore_max !== null && `max ${v.valore_max}`}
                            </span>
                          )}
                          {(v.salta_sabato || v.salta_domenica) && (
                            <span style={{ marginLeft: 6, color: LT.n500 }}>
                              · salta {v.salta_sabato && 'sab'}{v.salta_sabato && v.salta_domenica && '+'}{v.salta_domenica && 'dom'}
                            </span>
                          )}
                        </div>
                      </td>
                      {giorniSettimana.map((d, i) => {
                        const dIso = isoDate(d);
                        const sc = statoCella(v, dIso);
                        const stato = sc.stato;
                        const esec  = sc.esec;
                        const tooltip = esec ?
                          `${esec.operatore_nome || ''} · ${(typeof esec.ora_effettuazione === 'string' ? esec.ora_effettuazione.substring(0,5) : '')}` +
                          (esec.note ? `\n${esec.note}` : '') : '';
                        const disabledFutura = isSettimanaFutura;
                        const cellInteragibile = !disabledFutura && stato === 'da-fare-oggi';
                        return (
                          <td key={i} style={tdGiorno}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <button
                                disabled={!cellInteragibile && stato !== 'compilata-conforme' && stato !== 'compilata-nc'}
                                onClick={() => !disabledFutura && handleClickCella(v, dIso, stato)}
                                title={stato === 'da-fare-passato' ? 'Per chiudere questo buco, avvia una sessione con questa data' : tooltip}
                                style={{
                                  ...stileCella(stato),
                                  padding: 0,
                                  fontFamily: LT.sans,
                                }}
                              >
                                <CellaContent stato={stato} esec={esec} voce={v} />
                              </button>
                              {cellInteragibile && (
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    onClick={() => handleTapNonConforme(v)}
                                    title="Registra come non conforme"
                                    style={{
                                      flex: 1, height: 22, fontSize: 10, fontWeight: 700,
                                      color: LT.rosso, background: LT.rossoSoft,
                                      border: `1px solid ${LT.rosso}33`, borderRadius: LT.r.sm,
                                      cursor: 'pointer', padding: 0, fontFamily: LT.sans,
                                    }}
                                  >
                                    NC
                                  </button>
                                  <button
                                    onClick={() => handleTapSalta(v)}
                                    title="Marca come non applicabile oggi"
                                    style={{
                                      flex: 1, height: 22, fontSize: 10, fontWeight: 700,
                                      color: LT.n600, background: LT.n100,
                                      border: `1px solid ${LT.n200}`, borderRadius: LT.r.sm,
                                      cursor: 'pointer', padding: 0, fontFamily: LT.sans,
                                    }}
                                  >
                                    N.A.
                                  </button>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!caricando && voci.length === 0 && !errore && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 999,
            background: LT.bluSoft, color: LT.blu,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 14,
          }}>
            <Ico d={I.calendar || I.clock} size={32} />
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: LT.n800 }}>
            Nessuna attività giornaliera assegnata
          </div>
          <div style={{ fontSize: 13, color: LT.n500, marginTop: 4 }}>
            Solo le voci di scadenziario con frequenza giornaliera compaiono nella matrice.
            Le altre frequenze restano disponibili nella vista classica.
          </div>
        </div>
      )}

      {caricando && (
        <div style={{ textAlign: 'center', padding: 24, fontSize: 13, color: LT.n500 }}>
          Caricamento…
        </div>
      )}

      {voci.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 11, color: LT.n500, textAlign: 'center' }}>
          Voci con frequenza non giornaliera (settimanale, mensile, ecc.) restano nella vista classica.
        </div>
      )}
    </div>
  );
}

// Modale per avviare una sessione di registrazione a data/ora differenti
function FormSessione({ initData, initOra, oggiIso, errore, onAnnulla, onConferma }) {
  const [data, setData] = useState(initData);
  const [ora,  setOra ] = useState(initOra);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  };
  const modal = {
    background: '#fff', borderRadius: LT.r.lg, padding: 20,
    width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.25)',
    fontFamily: LT.sans,
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 12, marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.bluSoft, color: LT.blu,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.clock} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>Avvia sessione di registrazione</div>
            <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
              Data e ora vengono usate per tutte le registrazioni successive
            </div>
          </div>
        </div>

        <label style={labelStyle}>Data</label>
        <input style={inputStyle} type="date" max={oggiIso} value={data}
          onChange={e => setData(e.target.value)} />

        <label style={labelStyle}>Ora</label>
        <input style={inputStyle} type="time" value={ora}
          onChange={e => setOra(e.target.value)} />

        {errore && (
          <div style={{
            background: LT.rossoSoft, color: LT.rosso, borderRadius: LT.r.md,
            padding: '8px 12px', marginTop: 12, fontSize: 13, fontWeight: 500,
            display: 'flex', alignItems: 'flex-start', gap: 8,
          }}>
            <Ico d={I.alert} size={15} style={{ marginTop: 1, flexShrink: 0 }} />
            <span>{errore}</span>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="outline" block onClick={onAnnulla}>Annulla</Btn>
          <Btn kind="primary" block icon="play" onClick={() => onConferma(data, ora)}>
            Avvia sessione
          </Btn>
        </div>
      </div>
    </div>
  );
}

// (deprecato) Modale di conferma con orario — non più usato; mantenuto per
// retro-compatibilità in caso di import esterni futuri.
function _ConfermaConOrario({ ctx, oggiIso, onAnnulla, onConferma }) {
  const v = ctx.voce;
  const initData = ctx.dataIso;
  const now = new Date();
  const initOra = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}`;
  const [data, setData] = useState(initData);
  const [ora,  setOra ] = useState(initOra);

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  };
  const modal = {
    background: '#fff', borderRadius: LT.r.lg, padding: 20,
    width: '100%', maxWidth: 420, boxShadow: '0 20px 60px rgba(0,0,0,.25)',
    fontFamily: LT.sans,
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: LT.n500, textTransform: 'uppercase', letterSpacing: '.05em', marginTop: 10, marginBottom: 6 };
  const inputStyle = { width: '100%', padding: '10px 12px', fontSize: 14, fontFamily: LT.sans, border: `1px solid ${LT.n200}`, borderRadius: LT.r.md, background: '#fff', color: LT.n800, boxSizing: 'border-box', outline: 'none' };

  return (
    <div style={overlay}>
      <div style={modal}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: LT.r.md,
            background: LT.verdeSoft, color: LT.verde,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <Ico d={I.check} size={22} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: LT.n900 }}>
              Registra come conforme
            </div>
            <div style={{ fontSize: 12, color: LT.verde, fontWeight: 600, marginTop: 2 }}>
              Esito conforme
            </div>
          </div>
        </div>

        <div style={{ background: LT.n50, border: `1px solid ${LT.n150}`, borderRadius: LT.r.md, padding: '10px 12px', marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: LT.n900 }}>
            {v.oggetto}{v.collocazione ? ` — ${v.collocazione}` : ''}
          </div>
          <div style={{ fontSize: 12, color: LT.n500, marginTop: 2 }}>
            {v.ambito}{v.tipo_attivita ? ` · ${v.tipo_attivita}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Data</label>
            <input style={inputStyle} type="date" value={data} onChange={e => setData(e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Ora</label>
            <input style={inputStyle} type="time" value={ora} onChange={e => setOra(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
          <Btn kind="outline" block onClick={onAnnulla}>Annulla</Btn>
          <Btn kind="success" block icon="check" onClick={() => onConferma(data, ora)}>
            Conferma
          </Btn>
        </div>
      </div>
    </div>
  );
}

const thVoce = {
  position: 'sticky', left: 0, zIndex: 2,
  background: LT.n50, color: LT.n600,
  fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
  textAlign: 'left', padding: '10px 12px',
  borderBottom: `1px solid ${LT.n200}`,
  width: 280, minWidth: 260,
};
const thGiorno = {
  background: LT.n50, color: LT.n600,
  textAlign: 'center', padding: '8px 6px',
  borderBottom: `1px solid ${LT.n200}`,
  borderLeft: `1px solid ${LT.n150}`,
  width: 76, minWidth: 70,
};
const styleThOggi = {
  background: LT.bluSoft, color: LT.blu,
};
const tdAmbito = {
  background: LT.n50, color: LT.n800,
  fontSize: 12, fontWeight: 700, letterSpacing: '.02em', textTransform: 'uppercase',
  padding: '8px 12px',
  borderTop: `1px solid ${LT.n200}`, borderBottom: `1px solid ${LT.n200}`,
};
const tdTipo = {
  background: LT.bluSoft, color: LT.blu,
  fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
  padding: '6px 12px 6px 24px',
  borderBottom: `1px solid ${LT.n100}`,
};
const tdVoce = {
  position: 'sticky', left: 0, zIndex: 1,
  background: '#fff', padding: '10px 12px',
  borderBottom: `1px solid ${LT.n100}`,
  width: 280, minWidth: 260,
};
const tdGiorno = {
  padding: 4, textAlign: 'center',
  borderBottom: `1px solid ${LT.n100}`,
  borderLeft: `1px solid ${LT.n150}`,
  verticalAlign: 'top',
  minWidth: 70,
};
